import { useEffect, useState } from 'react';
import { CheckCircle2, ClipboardCheck } from 'lucide-react';
import { useProject } from '../../hooks/useProject';
import { fetchDataCompleteness } from '../../api/dataCompleteness';
import type { DataCompleteness, DataCompletenessMissing } from '../../types';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { getBackendError } from '../subcontractors/utils';

const MISSING_ROWS: Array<{ key: keyof DataCompletenessMissing; label: string }> = [
  { key: 'unsigned_daily_reports', label: 'İmzasız günlük rapor' },
  { key: 'overdue_inspections', label: 'Geciken denetim' },
  { key: 'missing_attendance_days', label: 'Eksik puantaj günü' },
  { key: 'tasks_without_due_date', label: 'Tarihsiz görev' },
];

/** Skor rengi: ≥80 yeşil, ≥50 amber, <50 kırmızı. */
function scoreColor(score: number): { text: string; stroke: string } {
  if (score >= 80) return { text: 'text-emerald-600', stroke: '#10b981' };
  if (score >= 50) return { text: 'text-amber-600', stroke: '#f59e0b' };
  return { text: 'text-red-600', stroke: '#ef4444' };
}

const RING_RADIUS = 40;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/**
 * Veri tamlık skoru kartı (Paket B) — seçili projenin son 30 gündeki eksik
 * verilerini ağırlıklı ceza skoru (0-100) ve kırılımıyla gösterir.
 */
export function DataCompletenessCard() {
  const { selectedProject } = useProject();
  const [data, setData] = useState<DataCompleteness | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedProject) {
      setData(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchDataCompleteness(selectedProject.id)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(getBackendError(err) || 'Veri tamlık skoru yüklenirken bir hata oluştu.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProject]);

  const color = data ? scoreColor(data.score) : null;
  const dashOffset = data
    ? RING_CIRCUMFERENCE * (1 - Math.min(100, Math.max(0, data.score)) / 100)
    : RING_CIRCUMFERENCE;

  return (
    <div className="rounded-xl bg-white p-5 shadow-sm" data-testid="data-completeness-card">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">Veri Tamlık Skoru</h3>
        <ClipboardCheck className="h-5 w-5 text-slate-400" aria-hidden="true" />
      </div>

      {!selectedProject ? (
        <div className="text-sm text-slate-500">Skoru görmek için bir proje seçin.</div>
      ) : loading ? (
        <div className="flex h-32 items-center justify-center">
          <LoadingSpinner size="sm" />
        </div>
      ) : error ? (
        <p className="text-sm text-red-600" data-testid="data-completeness-error" role="alert">
          {error}
        </p>
      ) : !data || !color ? null : (
        <>
          <div className="flex items-center gap-4">
            {/* Skor halkası */}
            <div className="relative h-24 w-24 shrink-0">
              <svg viewBox="0 0 96 96" className="h-24 w-24 -rotate-90">
                <circle
                  cx="48"
                  cy="48"
                  r={RING_RADIUS}
                  fill="none"
                  stroke="#e2e8f0"
                  strokeWidth="8"
                />
                <circle
                  cx="48"
                  cy="48"
                  r={RING_RADIUS}
                  fill="none"
                  stroke={color.stroke}
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={RING_CIRCUMFERENCE}
                  strokeDashoffset={dashOffset}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span
                  className={`text-2xl font-bold ${color.text}`}
                  data-testid="data-completeness-score"
                >
                  {data.score}
                </span>
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-700">
                {data.score >= 80
                  ? 'Veriler raporlamaya hazır.'
                  : data.score >= 50
                    ? 'Bazı eksik veriler var.'
                    : 'Önemli veri eksikleri var.'}
              </p>
              <p className="mt-1 text-xs text-slate-400" data-testid="data-completeness-period">
                Son {data.period_days} gün
              </p>
            </div>
          </div>

          {/* Eksik kırılımı */}
          <div className="mt-4 space-y-2">
            {MISSING_ROWS.map(({ key, label }) => {
              const count = data.missing[key] ?? 0;
              return (
                <div
                  key={key}
                  className="flex items-center justify-between gap-2 text-sm"
                  data-testid={`data-completeness-missing-${key}`}
                >
                  <span className="text-slate-600">{label}</span>
                  {count === 0 ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" aria-label="Eksik yok" />
                  ) : (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                      {count}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
